import { Checkbox, Chip } from "@mui/material";
import { ColumnDef, Row } from "@tanstack/react-table";
import { Eye, Pencil, PenOff, X } from "lucide-react";
import i18n from "@lib/i18n";
import ActionButton from "@components/ActionButton";
import { Binding } from "@lib/types/binding";
import { differenceInHours, differenceInDays } from "date-fns";

const getInterval = (start: string, end: string) => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const hoursDiff = differenceInHours(endDate, startDate);
  const daysDiff = differenceInDays(endDate, startDate);

  if (daysDiff >= 1) {
    return `${daysDiff} ${i18n.t(
      daysDiff === 1 ? "user.bindings.day" : "user.bindings.days"
    )}`;
  }
  return `${hoursDiff} ${i18n.t(
    hoursDiff === 1 ? "user.bindings.hour" : "user.bindings.hours"
  )}`;
};

export const columns = (
  setBindingToEdit: (binding: Binding) => void,
  viewBinding: (binding: Binding) => void,
  deleteBinding: (binding: Binding) => void,
  setUpdateBindingDialogOpen: (open: boolean) => void,
  bindingType: string
): ColumnDef<Binding>[] => [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={() => table.toggleAllRowsSelected()}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onChange={() => row.toggleSelected()}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    meta: { hideOnSmallScreens: true },
  },
  {
    accessorKey: "driver_details.name",
    header: i18n.t("table.driver"),
    cell: ({ row }) => (
      <span className="text-neutral-600 leading-none font-book text-base">
        {row.original.driver_details?.name ||
          (row.original.driver_group_id !== null ? (
            "-"
          ) : (
            <span className="text-red-500 italic">
              {i18n.t("user.bindings.deletedDriver")}
            </span>
          ))}
      </span>
    ),
    enableSorting: true,
  },
  {
    accessorKey: "parameter_number",
    header: i18n.t("table.parameterNumber"),
    cell: ({ row }) => (
      <span className="text-neutral-600 leading-none font-book text-base">
        {row.original.parameter_number || "-"}
      </span>
    ),
    enableSorting: true,
    meta: { hideOnSmallScreens: true },
  },
  {
    accessorKey: "unit_details.name",
    header: i18n.t("table.unit"),
    cell: ({ row }) => (
      <span className="text-neutral-600 leading-none font-book text-base">
        {row.original.unit_details?.name ||
          (row.original.unit_group_details !== null ? (
            "-"
          ) : (
            <span className="text-red-500 italic">
              {i18n.t("user.bindings.deletedUnit")}
            </span>
          ))}
      </span>
    ),
    enableSorting: true,
    meta: { hideOnSmallScreens: true },
  },
  {
    accessorKey: "driver_group_details.name",
    header: i18n.t("table.driverGroup"),
    cell: ({ row }) => (
      <span className="text-neutral-600 leading-none font-book text-base">
        {row.original.driver_group_details?.name || "-"}
      </span>
    ),
    enableSorting: true,
  },

  {
    accessorKey: "unit_group_details.name",
    header: i18n.t("table.unitGroup"),
    cell: ({ row }) => (
      <span className="text-neutral-600 leading-none font-book text-base">
        {row.original.unit_group_details?.name || "-"}
      </span>
    ),
    enableSorting: true,
    meta: { hideOnSmallScreens: true },
  },

  ...(bindingType === "one_time"
    ? [
        {
          accessorKey: "start_datetime",
          header: i18n.t("table.startDate"),
          cell: ({ row }: { row: Row<Binding> }) => (
            <span className="text-neutral-600 leading-none font-book text-base">
              {row.original.start_datetime
                ? new Date(row.original.start_datetime).toLocaleString()
                : "-"}
            </span>
          ),
          enableSorting: true,
          meta: { hideOnSmallScreens: true },
        },
        {
          accessorKey: "end_datetime",
          header: i18n.t("table.endDate"),
          cell: ({ row }: { row: Row<Binding> }) => (
            <span className="text-neutral-600 leading-none font-book text-base">
              {row.original.end_datetime && row.original.start_datetime ? (
                <>{new Date(row.original.end_datetime).toLocaleString()}</>
              ) : (
                "-"
              )}
            </span>
          ),
          enableSorting: true,
          meta: { hideOnSmallScreens: true },
        },
      ]
    : []),

  ...(bindingType === "expiring"
    ? [
        {
          accessorKey: "end_datetime",
          header: i18n.t("table.endDate"),
          cell: ({ row }: { row: Row<Binding> }) => (
            <span className="text-neutral-600 leading-none font-book text-base">
              {row.original.end_datetime && row.original.start_datetime ? (
                <>
                  {new Date(row.original.end_datetime).toLocaleString()}
                  <span className="text-neutral-400 ml-2">
                    (
                    {getInterval(
                      row.original.start_datetime,
                      row.original.end_datetime
                    )}
                    )
                  </span>
                </>
              ) : (
                "-"
              )}
            </span>
          ),
          enableSorting: true,
          meta: { hideOnSmallScreens: true },
        },
      ]
    : []),
  {
    accessorKey: "status",
    header: i18n.t("table.status"),
    cell: ({ row }) => (
      <span className="text-neutral-600 leading-none font-book text-base">
        <Chip
          label={i18n.t(`status.${row.original.status}`)}
          color={row.original.status === "active" ? "success" : "error"}
        />
      </span>
    ),
    enableSorting: true,
  },
  {
    id: "actions",
    header: i18n.t("table.actions"),
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <div className="flex w-full items-center justify-center gap-1">
          <ActionButton
            title={i18n.t("general.view")}
            onClick={() => viewBinding(row.original)}
          >
            <Eye size="20" />
          </ActionButton>
          <ActionButton
            title={i18n.t("general.edit")}
            onClick={() => {
              setBindingToEdit(row.original);
              setUpdateBindingDialogOpen(true);
            }}
            disabled={
              !(
                (row.original.unit_details ||
                  row.original.unit_group_details) &&
                (row.original.driver_details ||
                  row.original.driver_group_details)
              )
            }
          >
            {(row.original.unit_details || row.original.unit_group_details) &&
            (row.original.driver_details ||
              row.original.driver_group_details) ? (
              <Pencil size="20" />
            ) : (
              <PenOff size="20" />
            )}
          </ActionButton>

          <ActionButton
            title={i18n.t("general.delete")}
            onClick={() => deleteBinding(row.original)}
          >
            <X size="20" color="red" />
          </ActionButton>
        </div>
      );
    },
  },
];
